import React, { useEffect, useState } from 'react';

import { Form, Formik, FormikValues, useFormikContext } from 'formik';
import * as yup from 'yup';

import Field from 'components/Field';
import InputSecret from 'components/InputSecret';
import Header from 'components/LogoHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';

import {
  AcceptButton,
  Body,
  Container,
  GroupButton,
  RuleIconFail,
  RuleIconOk,
  RuleItem,
  RulesList,
  TextHeader,
} from './styles';

export interface Props {
  loading: boolean;
  onSubmit: (password: string) => void;
}

const SPECIAL_CHARS = /[!¡”#$%/,*.@&()\-_=+{}\[\]|':;¿?<>\^`]/;

const containsSequentialNumbers = (str: string): boolean => {
  for (let i = 0; i < str.length - 1; i++) {
    const current = parseInt(str[i]);
    const next = parseInt(str[i + 1]);
    if (!isNaN(current) && !isNaN(next)) {
      if (next === current + 1 || next === current - 1) {
        return true;
      }
    }
  }
  return false;
};

const ResetPassword = ({ loading, onSubmit }: Props) => {
  const { t } = useStoreTranslation();

  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .required()
      .test('password-validation', 'Invalid password', (value) => {
        const password = value || '';
        const hasLength = password.length >= 8 && password.length <= 12;
        const hasUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        return hasLength && hasUpperLower && hasNumber;
      }),
    confirmPassword: yup
      .string()
      .required()
      .oneOf([yup.ref('password')], t('resetPassword.passwordMismatch')),
  });

  const initialValues = {
    password: '',
    confirmPassword: '',
  };

  const validatePassword = (password: string) => {
    const isEmpty = password.trim().length === 0;

    return [
      {
        name: 'length',
        title: t('userForm.accessSystem.length.title'),
        message: t('userForm.accessSystem.length.message'),
        valid: isEmpty ? false : password.length >= 8 && password.length <= 12,
      },
      {
        name: 'uppercase-char',
        title: t('userForm.accessSystem.uppercaseChar.title'),
        message: t('userForm.accessSystem.uppercaseChar.message'),
        valid: isEmpty ? false : /[a-z]/.test(password) && /[A-Z]/.test(password),
      },
      {
        name: 'numeric-char',
        title: t('userForm.accessSystem.numericChar.title'),
        message: t('userForm.accessSystem.numericChar.message'),
        valid: isEmpty ? false : /\d/.test(password),
      },
    ];
  };

  const PasswordValidation = () => {
    const { values } = useFormikContext<{ password: string }>();
    const [rules, setRules] = useState(() => validatePassword(''));

    useEffect(() => {
      setRules(validatePassword(values.password || ''));
    }, [values.password]);

    if (!values.password) return null;

    return (
      <RulesList>
        {rules.map((rule) => (
          <RuleItem key={rule.name}>
            {rule.valid ? <RuleIconOk>✔</RuleIconOk> : <RuleIconFail>✖</RuleIconFail>}
            <span>
              <b>{rule.title}: </b>
              {rule.message}
            </span>
          </RuleItem>
        ))}
      </RulesList>
    );
  };

  const handleSubmit = (data: { password: string }) => {
    onSubmit(data.password);
  };

  return (
    <Container>
      <Formik
        initialStatus={{ rightCheckIfIsValid: true }}
        initialValues={initialValues}
        isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid }: FormikValues) => (
          <Form>
            <Header />
            <Body>
              <TextHeader>
                <h3>{t('resetPassword.title')}</h3>
              </TextHeader>
              <Field name="password" labelKey="resetPassword.password">
                <InputSecret id="password" placeholder={t('resetPassword.passwordPlaceholder')} />
              </Field>
              <PasswordValidation />
              <Field name="confirmPassword" labelKey="resetPassword.confirmPassword">
                <InputSecret id="confirmPassword" placeholder={t('resetPassword.confirmPasswordPlaceholder')} />
              </Field>
              <GroupButton>
                <AcceptButton
                  disabled={!isValid || loading}
                  value={t('resetPassword.submitButton')}
                  loading={loading}
                />
              </GroupButton>
            </Body>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default ResetPassword;
